import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  subscribeAiHosted,
  getAiHostedVersion,
  getAllowanceState,
  fetchAllowance,
  setAiIdentity,
  creditsView,
  weightOf,
  getEffectiveAiMode,
  getAiModePreference,
  byokAllowed,
  getConsent,
} from '../../lib/aiHosted';

/** Re-render on any hosted-AI state change (mode, consent, allowance, gate). */
export function useAiHostedVersion() {
  return useSyncExternalStore(subscribeAiHosted, getAiHostedVersion, getAiHostedVersion);
}

/**
 * Live hosted-AI state for the current user:
 * `{ data, status, credits, mode, preference, byokAllowed, consent, weight(feature), refresh }`.
 * Also keeps lib/aiHosted's identity in sync with AuthContext and loads `/ai/allowance`.
 */
export default function useAiAllowance() {
  const { user, plan, loading } = useAuth();
  const v = useAiHostedVersion();
  const userId = user ? user._id || user.id || user.email || 'user' : null;

  useEffect(() => {
    if (loading) return;
    setAiIdentity(userId, plan);
    fetchAllowance();
  }, [loading, userId, plan]);

  return useMemo(() => {
    const state = getAllowanceState();
    const data = state.data;
    return {
      data,
      status: state.status,
      error: state.error,
      credits: creditsView(data),
      mode: getEffectiveAiMode(data),
      preference: getAiModePreference(),
      byokAllowed: byokAllowed(data),
      consent: getConsent(),
      weight: (feature) => weightOf(feature, data),
      refresh: () => fetchAllowance({ force: true }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);
}
